/**
 * Team Nutrition Dashboard — Phase 2.1B
 *
 * Bound to: GET /api/nutrition-analytics/team/{team_id}
 *
 * Sections:
 *   1. Team header + KPI strip (avg weight / BF / lean / BMR / TDEE)
 *   2. Distributions (weight + BF % histograms)
 *   3. Top lists (heaviest / lowest BF / highest TDEE)
 *   4. Risk Athletes list (4 deterministic rules from config)
 *   5. Roster table with deep-link to AthleteNutritionDashboard
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Apple, Loader2, AlertTriangle, TrendingUp, Users,
} from 'lucide-react';
import {
  Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const fmt = (v, dp = 1, suffix = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${suffix}`;
const fmtInt = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`;

const StatCard = ({ label, value, testid }) => (
  <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
    <div className="text-2xl font-bold text-cyan-300">{value}</div>
  </Card>
);

const RULE_LABELS = {
  RAPID_WEIGHT_LOSS: { en: 'Rapid Weight Loss', it: 'Calo peso rapido', color: 'border-red-500/50 text-red-300 bg-red-500/10' },
  RAPID_WEIGHT_GAIN: { en: 'Rapid Weight Gain', it: 'Aumento peso rapido', color: 'border-amber-500/50 text-amber-300 bg-amber-500/10' },
  HIGH_BF_PCT: { en: 'High Body Fat %', it: 'BF % alto', color: 'border-orange-500/50 text-orange-300 bg-orange-500/10' },
  LOW_LEAN_MASS: { en: 'Low Lean Mass', it: 'Massa magra bassa', color: 'border-purple-500/50 text-purple-300 bg-purple-500/10' },
};

const TeamNutritionDashboardPage = () => {
  const { teamId } = useParams();
  const { api } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/nutrition-analytics/team/${teamId}`);
        setData(res.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, teamId]);

  if (loading) {
    return (
      <div className="p-12 flex items-center justify-center text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        {language === 'IT' ? 'Caricamento…' : 'Loading…'}
      </div>
    );
  }
  if (!data || data.error) {
    return (
      <div className="p-12 text-center text-zinc-400" data-testid="team-nutrition-error">
        {language === 'IT' ? 'Squadra non trovata o accesso negato' : 'Team not found or access denied'}
      </div>
    );
  }

  const team = data.team;
  const avg = data.averages || {};
  const dists = data.distributions || {};
  const risks = data.risk_athletes || [];
  const top = data.top_lists || {};
  const athletes = data.athletes || [];

  return (
    <div className="space-y-6 p-6" data-testid="team-nutrition-dashboard">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/staff/nutrition')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {language === 'IT' ? 'Indietro' : 'Back'}
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Apple className="w-6 h-6 text-emerald-400" />
              <h1 className="text-3xl font-bold text-zinc-100">{team.name}</h1>
              {team.code && (
                <Badge variant="outline" className="border-zinc-700 text-zinc-400">{team.code}</Badge>
              )}
            </div>
            <p className="text-sm text-zinc-400">
              {language === 'IT' ? 'Dashboard nutrizione' : 'Nutrition dashboard'} ·{' '}
              {data.summary_count} {language === 'IT' ? 'atleti' : 'athletes'}
            </p>
          </div>
        </div>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard testid="kpi-avg-weight" label={language === 'IT' ? 'Peso medio' : 'Avg weight'} value={fmt(avg.weight_kg, 1, ' kg')} />
        <StatCard testid="kpi-avg-bf" label="BF % medio" value={fmt(avg.body_fat_pct, 1, ' %')} />
        <StatCard testid="kpi-avg-lean" label={language === 'IT' ? 'Magra media' : 'Avg lean'} value={fmt(avg.lean_mass_kg, 1, ' kg')} />
        <StatCard testid="kpi-avg-bmr" label="BMR medio" value={fmtInt(avg.bmr_kcal, ' kcal')} />
        <StatCard testid="kpi-avg-tdee" label="TDEE medio" value={fmtInt(avg.tdee_kcal, ' kcal')} />
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="distributions">
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h2 className="text-lg font-semibold text-zinc-100 mb-3">
            {language === 'IT' ? 'Distribuzione peso (kg)' : 'Weight distribution (kg)'}
          </h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dists.weight_kg || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a' }} />
                <Bar dataKey="count" fill="#06b6d4" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h2 className="text-lg font-semibold text-zinc-100 mb-3">
            {language === 'IT' ? 'Distribuzione BF %' : 'BF % distribution'}
          </h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dists.body_fat_pct || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 11 }} />
                <YAxis stroke="#71717a" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a' }} />
                <Bar dataKey="count" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Top lists */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="top-lists">
        {[
          { key: 'heaviest', label: language === 'IT' ? 'Più pesanti' : 'Heaviest', unit: ' kg' },
          { key: 'lowest_body_fat', label: language === 'IT' ? 'BF % più basso' : 'Lowest BF %', unit: ' %' },
          { key: 'highest_tdee', label: language === 'IT' ? 'TDEE più alto' : 'Highest TDEE', unit: ' kcal' },
        ].map((cfg) => (
          <Card key={cfg.key} className="bg-zinc-900 border-zinc-800 p-5">
            <h3 className="text-sm font-semibold text-zinc-300 mb-3 uppercase tracking-wider">{cfg.label}</h3>
            <ol className="space-y-2">
              {(top[cfg.key] || []).map((row, i) => (
                <li key={row.athlete_id} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-300">{i + 1}. {row.name}</span>
                  <span className="text-cyan-300 font-medium">{fmt(row.value, 1, cfg.unit)}</span>
                </li>
              ))}
              {!top[cfg.key]?.length && <li className="text-zinc-500 text-sm">—</li>}
            </ol>
          </Card>
        ))}
      </div>

      {/* Risk Athletes */}
      <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="risk-athletes">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            {language === 'IT' ? 'Atleti a rischio' : 'Risk Athletes'}
            <Badge variant="outline" className="border-amber-500/50 text-amber-300 ml-2">
              {risks.length}
            </Badge>
          </h2>
          <span className="text-xs text-zinc-500">
            {language === 'IT' ? 'Soglie dal config' : 'Thresholds from config'} ({data.config_resolved_for_sport})
          </span>
        </div>
        {risks.length === 0 ? (
          <p className="text-sm text-zinc-500">
            {language === 'IT' ? 'Nessun atleta a rischio.' : 'No flagged athletes.'}
          </p>
        ) : (
          <div className="space-y-3">
            {risks.map((r) => (
              <div
                key={r.athlete_id}
                className="p-4 bg-zinc-950 rounded border border-zinc-800 hover:bg-zinc-900 transition-colors"
                data-testid={`risk-row-${r.athlete_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/staff/nutrition/athletes/${r.athlete_id}`)}
                  >
                    <div className="text-zinc-100 font-medium">{r.first_name} {r.last_name}</div>
                    <div className="text-xs text-zinc-500">{r.primary_position}</div>
                  </div>
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40">
                    {r.severity_score} {language === 'IT' ? 'segnalazioni' : 'flags'}
                  </Badge>
                </div>
                <div className="space-y-2" data-testid={`risk-flags-${r.athlete_id}`}>
                  {r.flags.map((f, i) => {
                    const cfg = RULE_LABELS[f.rule] || { en: f.rule, it: f.rule, color: 'border-zinc-700 text-zinc-300 bg-zinc-800/30' };
                    return (
                      <div key={i} className="bg-zinc-900 border border-zinc-800 rounded p-2.5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                          <Badge variant="outline" className={cfg.color}>
                            {language === 'IT' ? cfg.it : cfg.en}
                          </Badge>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500">
                              {language === 'IT' ? 'Valore' : 'Value'}:{' '}
                              <span className="text-zinc-200 font-medium">
                                {f.value_pct !== null && f.value_pct !== undefined ? `${f.value_pct.toFixed(1)}%` : '—'}
                              </span>
                            </span>
                            <span className="text-zinc-600">•</span>
                            <span className="text-zinc-500">
                              {language === 'IT' ? 'Soglia' : 'Threshold'}:{' '}
                              <span className="text-zinc-200 font-medium">
                                {f.threshold_pct !== null && f.threshold_pct !== undefined ? `${f.threshold_pct.toFixed(1)}%` : '—'}
                              </span>
                            </span>
                            {f.source && (
                              <>
                                <span className="text-zinc-600">•</span>
                                <Badge variant="outline" className={
                                  f.source === 'ORG_OVERRIDE' ? 'border-cyan-500/40 text-cyan-300 bg-cyan-500/10' :
                                  f.source === 'SPORT_OVERRIDE' ? 'border-purple-500/40 text-purple-300 bg-purple-500/10' :
                                  'border-zinc-700 text-zinc-400'
                                }>
                                  {f.source_label || f.source}
                                </Badge>
                              </>
                            )}
                          </div>
                        </div>
                        {f.explanation && (
                          <p className="text-xs text-zinc-400 mt-1">{f.explanation}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Roster table */}
      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="team-roster-table">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-zinc-400" />
            {language === 'IT' ? 'Roster nutrizionale' : 'Nutrition roster'}
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">{language === 'IT' ? 'Atleta' : 'Athlete'}</th>
                <th className="px-4 py-3 text-left">{language === 'IT' ? 'Posizione' : 'Position'}</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Peso' : 'Weight'}</th>
                <th className="px-4 py-3 text-right">BF %</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Magra' : 'Lean'}</th>
                <th className="px-4 py-3 text-right">TDEE</th>
                <th className="px-4 py-3 text-right">{language === 'IT' ? 'Target' : 'Target'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {athletes.map((a) => (
                <tr
                  key={a.athlete_id}
                  className="hover:bg-zinc-800/60 cursor-pointer transition-colors"
                  onClick={() => navigate(`/staff/nutrition/athletes/${a.athlete_id}`)}
                  data-testid={`roster-row-${a.athlete_id}`}
                >
                  <td className="px-4 py-3 text-zinc-100">{a.first_name} {a.last_name}</td>
                  <td className="px-4 py-3 text-zinc-400">{a.primary_position || '—'}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_weight_kg)}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_bf_pct)}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_lean_mass_kg)}</td>
                  <td className="px-4 py-3 text-right text-zinc-200">{fmtInt(a.latest_tdee_kcal)}</td>
                  <td className="px-4 py-3 text-right">
                    {a.active_target_kcal ? (
                      <span className="text-emerald-400">{fmtInt(a.active_target_kcal, ' kcal')}</span>
                    ) : <span className="text-zinc-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default TeamNutritionDashboardPage;
